import { createFileRoute } from '@tanstack/react-router'
import { locationByCode } from '@/lib/locations'
import {
  buildRegionCards,
  cardState,
  continentHeading,
  groupRegionCards,
  locationSummaryLine,
  type RegionCard,
} from '@/lib/status-regions'
import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  statusV2,
  StatusApiError,
  type StatusMonitor,
  type StatusIncident,
  type StatusMaintenance,
  type StatusMonitorRegions,
  type StatusPageTheme,
} from '@/lib/status-v2'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { CatSleeping, CatAlert } from '@/components/mascot'
import { useStatusPageEvents } from '@/hooks/use-status-events'
import { Flag } from '@/components/flag'
import { loadStatusPage, statusPageHead, OWN_STATUS_SLUG } from '@/lib/status-head'

export const Route = createFileRoute('/status/$slug')({
  component: PublicStatusPage,
  // The public status JSON is read on the server, so the first HTML already has the heading,
  // the status and the services (search engines and link previews read it without running scripts).
  loader: ({ params }) => loadStatusPage(params.slug),
  head: ({ params, loaderData }) => statusPageHead(params.slug, loaderData),
})

function PublicStatusPage() {
  const { slug } = Route.useParams()
  const loaded = Route.useLoaderData()

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-status-page', slug],
    queryFn: () => statusV2.getStatusPage(slug),
    // The page the server read: no second request on load, and the 30 s poll goes on from there.
    ...(loaded?.state === 'ok' ? { initialData: { data: loaded.page }, initialDataUpdatedAt: loaded.fetchedAt, staleTime: 30_000 } : {}),
    // A missing page will not appear on retry: show "not found" at once.
    retry: (count, err) => !(err instanceof StatusApiError && err.status === 404) && count < 2,
    refetchInterval: (query) => (query.state.error ? false : 30_000),
  })

  // Daily-uptime sparkline source: the v2 /history endpoint (per-monitor daily
  // series). NB the v2 /uptime endpoint returns only a single percentage, which
  // is NOT the sparkline source — history is.
  const { data: historyData } = useQuery({
    queryKey: ['public-status-history', slug],
    queryFn: () => statusV2.getHistory(slug),
    staleTime: 5 * 60 * 1000,
    enabled: !!data?.data,
  })

  const { data: regionsData } = useQuery({
    queryKey: ['public-status-regions', slug],
    queryFn: () => statusV2.getRegions(slug),
    refetchInterval: 30_000,
    enabled: !!data?.data,
  })

  // Per-monitor daily series keyed by monitor name (robust to ordering between
  // the show + history responses), mapped to the StatusUptimeBar shape.
  const historyByName = useMemo(() => {
    const m = new Map<string, Array<{ date: string; uptime: number }>>()
    for (const mon of historyData?.monitors ?? []) {
      m.set(
        mon.name,
        mon.history.map((d) => ({ date: d.date, uptime: d.uptime_percent })),
      )
    }
    return m
  }, [historyData])

  // Live updates: SSE from the Rust backend invalidates the queries on any
  // incident change for this page's monitors (the 30s polls above stay as a
  // fallback). Enabled once the page has loaded + is valid.
  useStatusPageEvents(slug, !!data?.data)

  if (isLoading) {
    // Uptrack's own page keeps its heading while the data loads; a crawler that cannot read the
    // API still gets an H1 and the description in the server HTML.
    if (slug === OWN_STATUS_SLUG) {
      return (
        <div className="mx-auto min-h-screen max-w-3xl px-4 py-8">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-semibold">Uptrack Status</h1>
            <p className="mt-2 text-muted-foreground">Live status of the Uptrack API, website and status page.</p>
          </div>
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        </div>
      )
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (error || !data?.data) {
    const notFound = error instanceof StatusApiError && error.status === 404
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold">{notFound || !error ? 'Status page not found' : 'Status page unavailable'}</h1>
          <p className="mt-2 text-muted-foreground">
            {notFound || !error
              ? 'This status page does not exist or is not public. Check the address, or ask the service owner for the correct link.'
              : 'We could not load this status page right now. Try again in a minute.'}
          </p>
          <a href="https://uptrack.app" className="mt-6 inline-block text-sm text-primary underline underline-offset-4">
            Status pages by Uptrack
          </a>
        </div>
      </div>
    )
  }

  const page = data.data
  // Every monitor of the page is "unknown" (or the page has none): none has a recent sample, for
  // example because the owner paused them. The API still counts no failure and says "operational", which would tell
  // the visitors that all systems work when nothing is being checked.
  const noData = page.monitors.every((m) => m.status === 'unknown')

  // theme_config jsonb is `unknown` in the v2 schema; narrow to its known shape.
  const theme = (page.theme ?? {}) as StatusPageTheme
  const themeStyle: React.CSSProperties = {
    ...(theme.background_color ? { backgroundColor: theme.background_color } : {}),
    ...(theme.text_color ? { color: theme.text_color } : {}),
    ...(theme.font_family ? { fontFamily: theme.font_family } : {}),
  }

  return (
    <div className="mx-auto min-h-screen max-w-3xl px-4 py-8" style={themeStyle}>
      {/* Header */}
      <div className="mb-8 text-center">
        {page.logo_url && (
          <img
            src={page.logo_url}
            alt={page.name}
            className="mx-auto mb-4 h-12"
          />
        )}
        <h1 className="text-3xl font-semibold">{page.name}</h1>
        {page.description && (
          <p className="mt-2 text-muted-foreground">{page.description}</p>
        )}
      </div>

      {/* Overall Status Banner */}
      {noData ? <NoDataBanner /> : <OverallStatusBanner status={page.overall_status} uptime={page.uptime_percentage} />}

      {/* Maintenance Banners */}
      {page.maintenance_windows?.length > 0 && (
        <div className="mb-6 space-y-3">
          {page.maintenance_windows.map((mw, i) => (
            <MaintenanceBanner key={i} window={mw} />
          ))}
        </div>
      )}

      {/* Monitor List */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Services</CardTitle>
        </CardHeader>
        <CardContent>
          {page.monitors.length === 0 ? (
            <p className="text-muted-foreground">No monitors configured.</p>
          ) : (
            <div className="divide-y">
              {page.monitors.map((monitor) => (
                <MonitorRow
                  key={monitor.name}
                  monitor={monitor}
                  dailyUptime={historyByName.get(monitor.name)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Regional Response Times */}
      <RegionalResponseTimes regionsData={regionsData?.monitors} />

      {/* Recent Incidents */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Incidents</CardTitle>
        </CardHeader>
        <CardContent>
          {page.recent_incidents.length === 0 ? (
            <p className="text-muted-foreground">No recent incidents.</p>
          ) : (
            <div className="space-y-4">
              {page.recent_incidents.map((incident) => (
                <IncidentRow key={incident.id} incident={incident} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription Widget */}
      {page.allow_subscriptions && (
        <SubscribeWidget slug={slug} />
      )}

      {/* Footer — hidden when whitelabel is enabled */}
      {!page.whitelabel && (
        <div className="mt-8 text-center text-sm text-muted-foreground">
          Powered by{' '}
          {/* Absolute URL: on a custom status domain a router link would point at that domain's root */}
          <a href="https://uptrack.app/?ref=status-page" className="font-medium text-primary hover:underline">
            Uptrack
          </a>
        </div>
      )}
    </div>
  )
}

function NoDataBanner() {
  return (
    <div className="mb-6 rounded-lg border border-border bg-muted p-6 text-center">
      <p className="text-lg font-semibold">No current data</p>
      <p className="mt-1 text-sm text-muted-foreground">
        These services are not being checked right now, so this page cannot say whether they work.
      </p>
    </div>
  )
}

function OverallStatusBanner({
  status,
  uptime,
}: {
  status: string
  /** `null` when the server cannot read the figure; never show a made-up number. */
  uptime: number | null | undefined
}) {
  const config: Record<string, { label: string; className: string }> = {
    operational: {
      label: 'All Systems Operational',
      className: 'border-status-up bg-status-up-soft text-status-up',
    },
    degraded: {
      label: 'Degraded Performance',
      className: 'border-status-degraded bg-status-degraded-soft text-status-degraded',
    },
    partial_outage: {
      label: 'Partial Outage',
      className: 'border-status-degraded bg-status-degraded-soft text-status-degraded',
    },
    major_outage: {
      label: 'Major Outage',
      className: 'border-status-down bg-status-down-soft text-status-down',
    },
    unknown: {
      label: 'Status unknown',
      className: 'border-border bg-card text-muted-foreground',
    },
  }

  // A status this page does not know must not read as operational.
  const { label, className } = config[status] ?? config.unknown

  const isHealthy = status === 'operational'

  return (
    <div className={`mb-6 rounded-lg border p-6 text-center ${className}`}>
      {isHealthy ? (
        <CatSleeping size={64} className="mx-auto mb-3" />
      ) : config[status] && status !== 'unknown' ? (
        <CatAlert size={64} className="mx-auto mb-3" />
      ) : null}
      <p className="text-lg font-semibold">{label}</p>
      <p className="mt-1 text-sm opacity-80">
        {typeof uptime === 'number'
          ? `${uptime.toFixed(2)}% uptime over the last 30 days`
          : 'Uptime figure unavailable'}
      </p>
    </div>
  )
}

function MaintenanceBanner({ window: mw }: { window: StatusMaintenance }) {
  const isActive = mw.status === 'active'
  const start = new Date(mw.start_time ?? '')
  const end = new Date(mw.end_time ?? '')

  const timeStr = isActive
    ? `Until ${end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
    : `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`

  return (
    <div
      className={`rounded-lg border p-4 ${
        isActive
          ? 'border-status-degraded bg-status-degraded-soft text-status-degraded'
          : 'border-status-paused bg-status-paused-soft text-status-paused'
      }`}
    >
      <div className="flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 shrink-0">
          <path fillRule="evenodd" d="M13.5 4.938a7 7 0 1 1-9.006 1.737c.28-.257.718-.08.718.279V14a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5V7.272a1 1 0 0 1 .76-.97l2.528-.643Z" clipRule="evenodd" />
        </svg>
        <div>
          <p className="font-medium">
            {isActive ? 'Maintenance in progress' : 'Upcoming maintenance'}
            {mw.title && `: ${mw.title}`}
          </p>
          <p className="text-sm opacity-80" suppressHydrationWarning>{timeStr}</p>
        </div>
      </div>
    </div>
  )
}

function MonitorRow({ monitor, dailyUptime }: { monitor: StatusMonitor; dailyUptime?: Array<{ date: string; uptime: number }> }) {
  return (
    <div className="py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <StatusDot status={monitor.status} />
          <div>
            <span className="font-medium">{monitor.name}</span>
            {monitor.last_checked_at && (
              <p className="text-xs text-muted-foreground" suppressHydrationWarning>
                Checked {timeAgo(new Date(monitor.last_checked_at))}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          {monitor.response_time != null && (
            <span>{monitor.response_time}ms</span>
          )}
          <MonitorStatusBadge status={monitor.status} />
        </div>
      </div>
      {dailyUptime && dailyUptime.length > 0 && (
        <div className="mt-2">
          <StatusUptimeBar data={dailyUptime} />
        </div>
      )}
    </div>
  )
}

function StatusUptimeBar({ data }: { data: Array<{ date: string; uptime: number }> }) {
  return (
    <div>
      <div className="flex gap-[1.5px]">
        {data.map((day) => {
          const color =
            day.uptime >= 99.5 ? 'bg-status-up' :
            day.uptime >= 95 ? 'bg-status-degraded' :
            day.uptime > 0 ? 'bg-status-down' :
            'bg-muted'

          return (
            <div
              key={day.date}
              className={`h-7 flex-1 rounded-[2px] ${color} transition-opacity hover:opacity-70`}
              title={`${new Date(day.date).toLocaleDateString()}\n${day.uptime.toFixed(2)}% uptime`}
            />
          )
        })}
      </div>
      <div className="mt-1 flex justify-between text-xs text-muted-foreground">
        <span>{data.length} days ago</span>
        <span>Today</span>
      </div>
    </div>
  )
}

function StatusDot({ status }: { status: string }) {
  const colors: Record<string, string> = {
    up: 'bg-status-up',
    down: 'bg-status-down',
    paused: 'bg-status-paused',
    unknown: 'bg-gray-400',
  }

  return (
    <span className="relative flex h-2.5 w-2.5">
      {status === 'up' && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-status-up opacity-75" />
      )}
      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${colors[status] ?? colors.unknown}`} />
    </span>
  )
}

function MonitorStatusBadge({ status }: { status: string }) {
  if (status === 'up') {
    return <Badge className="bg-status-up hover:bg-status-up">Operational</Badge>
  }
  if (status === 'down') {
    return <Badge variant="destructive">Down</Badge>
  }
  if (status === 'paused') {
    return <Badge variant="secondary">Paused</Badge>
  }
  return <Badge variant="secondary">Unknown</Badge>
}

function IncidentRow({ incident }: { incident: StatusIncident }) {
  const date = new Date(incident.started_at ?? '').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className="border-l-2 border-muted pl-4">
      <div className="flex items-center gap-2">
        <Badge variant={incident.status === 'resolved' ? 'secondary' : 'destructive'}>
          {incident.status}
        </Badge>
        <span className="text-sm text-muted-foreground" suppressHydrationWarning>{date}</span>
      </div>
      {incident.monitor_name && (
        <p className="mt-1 font-medium">{incident.monitor_name}</p>
      )}
      {incident.cause && (
        <p className="mt-1 text-sm text-muted-foreground">{incident.cause}</p>
      )}
      {incident.duration != null && incident.status === 'resolved' && (
        <p className="mt-1 text-xs text-muted-foreground">
          Resolved in {formatDuration(incident.duration)}
        </p>
      )}
    </div>
  )
}

function SubscribeWidget({ slug }: { slug: string }) {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const mutation = useMutation({
    mutationFn: (emailAddr: string) => statusV2.subscribe(slug, emailAddr),
    onSuccess: () => {
      setSubmitted(true)
      setEmail('')
    },
  })

  if (submitted) {
    return (
      <Card className="mb-6">
        <CardContent className="pt-6 text-center">
          <p className="font-medium text-status-up dark:text-status-up">
            Check your email to confirm your subscription.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Subscribe to Updates</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (email.trim()) mutation.mutate(email.trim())
          }}
          className="flex gap-2"
        >
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={mutation.isPending}
            className="flex-1"
          />
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </form>
        {mutation.isError && (
          <p className="mt-2 text-sm text-destructive">
            {mutation.error instanceof StatusApiError
              ? mutation.error.message
              : 'Failed to subscribe. Please try again.'}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  const hours = Math.floor(seconds / 3600)
  const mins = Math.round((seconds % 3600) / 60)
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

function RegionalResponseTimes({ regionsData }: { regionsData?: StatusMonitorRegions[] }) {
  const { groups, summary, measured } = useMemo(() => {
    const cards = buildRegionCards(regionsData)
    return {
      groups: groupRegionCards(cards),
      summary: locationSummaryLine(cards),
      measured: cards.some((c) => cardState(c) !== 'pending'),
    }
  }, [regionsData])

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Response time by location</CardTitle>
        {summary && <p className="text-sm text-body">{summary}</p>}
      </CardHeader>
      <CardContent className="space-y-4">
        {groups.map((g) => {
          const headingId = `region-${g.continent.toLowerCase().replace(/\s+/g, '-')}`
          return (
            <section key={g.continent} aria-labelledby={headingId}>
              <h3 id={headingId} className="mb-2 text-sm font-medium text-body">
                {continentHeading(g)}
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {g.cards.map((r) => (
                  <RegionCardView key={r.region} card={r} />
                ))}
              </div>
            </section>
          )
        })}
        {measured && (
          <p className="text-sm text-body">
            Each number is the average response time over the monitors that location checked. Monitors can use different locations.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function RegionCardView({ card: r }: { card: RegionCard }) {
  const state = cardState(r)
  const pending = state === 'pending'
  const dotClass =
    state === 'pending' ? 'bg-muted'
    : state === 'up' ? 'bg-status-up'
    : state === 'partial' && r.upCount / r.total > 0.9 ? 'bg-status-degraded'
    : 'bg-status-down'
  return (
    // On a phone one card is one row: name left, number and status right. From 640 px it stacks.
    <div className="flex min-w-0 items-center gap-3 rounded-lg border p-2 sm:p-3">
      <Flag flag={r.flag} className="shrink-0 text-xl" />
      <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-3 sm:block">
        <div className="min-w-0 break-words text-sm font-medium" title={locationByCode(r.region)?.country}>
          {locationByCode(r.region)?.city ?? r.label.split(',')[0]}
        </div>
        <div className="flex flex-wrap items-center gap-x-2">
          <span className={`num text-lg font-semibold ${pending ? 'text-muted-foreground' : ''}`}>
            {pending ? '—' : `${r.avgMs}ms`}
          </span>
          <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
          {!pending && (
            <span className="text-sm text-muted-foreground">{state === 'up' ? 'Up' : state === 'down' ? 'Down' : 'Some down'}</span>
          )}
          {pending && <span className="text-sm text-muted-foreground">pending</span>}
        </div>
      </div>
    </div>
  )
}

function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}
